import { useMutation, useQueryClient } from 'react-query';
import { useFirebase } from 'components/Firebase/index.js';
import { useAlert } from 'contexts/AlertContext.jsx';
import useOrgId from 'hooks/useOrgId.js';
import { productCatalogueServerUrl } from '../../../client-config.js';

/**
 * Create a product in the Product Catalogue service.
 * Supports both org-level and feed-scoped endpoints.
 *
 * POST /api/v1/orgs/:orgId/products
 * POST /api/v1/orgs/:orgId/feeds/:feedId/products
 *
 * Body must follow essentialProductSchema (see product-catalog service):
 * { id, title, price, link, image_link, availability?, quantity? }
 */
const useCreateCatalogueProductMutation = (options = {}) => {
  const firebase = useFirebase();
  const { setAlert } = useAlert();
  const orgId = useOrgId();
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options || {};

  return useMutation({
    mutationFn: async ({ product, feedId } = {}) => {
      if (!product || typeof product !== 'object') {
        throw new Error('Missing product payload');
      }
      const idToken = await firebase.getIdToken();
      const base = `${productCatalogueServerUrl}/api/v1/orgs/${orgId}`;
      const url = feedId
        ? `${base}/feeds/${encodeURIComponent(feedId)}/products`
        : `${base}/products`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(product),
      });

      if (response.ok) {
        return response.json();
      }

      throw {
        status: response.status,
        statusText: response.statusText,
        ...await response.json(),
      };
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(['products', orgId]);
      queryClient.invalidateQueries(['products-infinite', orgId]);
      onSuccess?.(data, variables, context);
      setAlert({ title: 'Product created' });
    },
    onError: (error) => {
      if (error?.status === 409) return;
      setAlert({ title: 'Failed to create product', variant: 'danger' });
    },
    ...rest,
  });
};

export default useCreateCatalogueProductMutation;

